<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import ImovelAPI from 'dashboard/api/imoveis';

import LaisField from './LaisField.vue';
import LaisPrimaryButton from './LaisPrimaryButton.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['normalized', 'update:modelValue']);

const isNormalizing = ref(false);
const isAutocompleteLoading = ref(false);
const searchQuery = ref('');
const fillMode = ref('address');
const predictions = ref([]);
const isPredictionListOpen = ref(false);
const sessionToken = ref(null);
let blurTimer = null;

const inputClass =
  'w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-lais-border rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50 placeholder:text-lais-text-muted transition-all';

const sourceLabel = computed(() => {
  if (props.modelValue?.address_source === 'google_places') {
    return 'Google Places';
  }

  if (props.modelValue?.address_source === 'regex') {
    return 'Fallback manual';
  }

  return '';
});

const buildQueryFromModel = () =>
  [
    props.modelValue?.endereco,
    props.modelValue?.numero,
    props.modelValue?.bairro,
    props.modelValue?.cidade,
    props.modelValue?.uf,
    props.modelValue?.cep,
  ]
    .filter(Boolean)
    .join(', ');

const createSessionToken = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `addr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const ensureSessionToken = () => {
  if (!sessionToken.value) {
    sessionToken.value = createSessionToken();
  }

  return sessionToken.value;
};

const clearPredictions = () => {
  predictions.value = [];
  isPredictionListOpen.value = false;
};

const resetSession = () => {
  sessionToken.value = null;
};

const emitValue = patch => {
  emit('update:modelValue', {
    ...props.modelValue,
    ...patch,
  });
};

const predictionPlaceId = prediction =>
  prediction?.placeId || prediction?.place_id || '';

const predictionFullText = prediction =>
  prediction?.fullText ||
  prediction?.full_text ||
  prediction?.description ||
  '';

const predictionPrimaryText = prediction =>
  prediction?.primaryText ||
  prediction?.primary_text ||
  predictionFullText(prediction);

const predictionSecondaryText = prediction =>
  prediction?.secondaryText || prediction?.secondary_text || '';

const normalize = async ({ googlePlaceId } = {}) => {
  const query = searchQuery.value.trim() || buildQueryFromModel();

  if (!query && !props.modelValue?.cep && !googlePlaceId) {
    useAlert('Informe um endereço ou CEP para normalizar.');
    return;
  }

  isNormalizing.value = true;
  try {
    const { data } = await ImovelAPI.normalizeAddress({
      query,
      cep: props.modelValue?.cep,
      google_place_id: googlePlaceId || props.modelValue?.google_place_id,
      session_token: sessionToken.value,
    });

    const endereco = [data.address, data.streetNumber]
      .filter(
        (item, index) =>
          Boolean(item) && (index === 0 || !props.modelValue?.sem_numero)
      )
      .join(', ')
      .trim();

    emitValue({
      endereco: data.address || endereco || props.modelValue?.endereco || '',
      numero:
        data.streetNumber ||
        props.modelValue?.numero ||
        (props.modelValue?.sem_numero ? '' : ''),
      sem_numero: props.modelValue?.sem_numero || !data.streetNumber,
      bairro: data.neighborhood || props.modelValue?.bairro || '',
      cidade: data.city || props.modelValue?.cidade || '',
      uf: data.state || props.modelValue?.uf || '',
      cep: data.zipCode || props.modelValue?.cep || '',
      latitude: data.lat ?? props.modelValue?.latitude ?? '',
      longitude: data.lng ?? props.modelValue?.longitude ?? '',
      google_place_id:
        data.googlePlaceId || props.modelValue?.google_place_id || '',
      endereco_completo:
        data.fullAddress || props.modelValue?.endereco_completo || query,
      address_source:
        data.source || props.modelValue?.address_source || 'regex',
    });

    searchQuery.value = data.fullAddress || query;
    clearPredictions();
    resetSession();
    emit('normalized', data);
  } catch {
    useAlert(
      'Não foi possível normalizar o endereço agora. Você pode preencher os campos manualmente abaixo.'
    );
  } finally {
    isNormalizing.value = false;
  }
};

const loadPredictions = async query => {
  if (fillMode.value !== 'address' || query.length < 3) {
    clearPredictions();
    return;
  }

  const activeToken = ensureSessionToken();
  isAutocompleteLoading.value = true;

  try {
    const { data } = await ImovelAPI.autocompleteAddress({
      query,
      session_token: activeToken,
    });

    if (searchQuery.value.trim() !== query) return;

    predictions.value = Array.isArray(data?.predictions)
      ? data.predictions
      : [];
    sessionToken.value = data?.sessionToken || activeToken;
    isPredictionListOpen.value = predictions.value.length > 0;
  } catch {
    clearPredictions();
  } finally {
    isAutocompleteLoading.value = false;
  }
};

const debouncedLoadPredictions = debounce(loadPredictions, 300, false);

const onSearchInput = () => {
  if (fillMode.value === 'cep') {
    clearPredictions();
    resetSession();
    return;
  }

  debouncedLoadPredictions(searchQuery.value.trim());
};

const onSearchFocus = () => {
  if (blurTimer) {
    clearTimeout(blurTimer);
    blurTimer = null;
  }

  if (fillMode.value === 'address') {
    ensureSessionToken();
    isPredictionListOpen.value = predictions.value.length > 0;
  }
};

const onSearchBlur = () => {
  blurTimer = window.setTimeout(() => {
    isPredictionListOpen.value = false;
  }, 150);
};

const selectPrediction = async prediction => {
  const placeId = predictionPlaceId(prediction);
  if (!placeId) return;

  searchQuery.value = predictionFullText(prediction);
  clearPredictions();
  await normalize({ googlePlaceId: placeId });
};

const setFillMode = mode => {
  fillMode.value = mode;
  clearPredictions();
  if (mode === 'cep') {
    resetSession();
  }
};

const toggleWithoutNumber = event => {
  emitValue({
    sem_numero: event.target.checked,
    numero: event.target.checked ? '' : props.modelValue?.numero || '',
  });
};

onUnmounted(() => {
  if (blurTimer) {
    clearTimeout(blurTimer);
  }
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
  >
    <div class="flex flex-col gap-3">
      <div class="grid gap-3 border-b border-lais-border-soft pb-4">
        <div
          class="inline-flex w-fit rounded-full border border-lais-border bg-lais-bg-soft p-1"
        >
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors"
            :class="
              fillMode === 'cep'
                ? 'bg-white text-lais-primary shadow-sm'
                : 'text-lais-text-muted hover:text-lais-primary'
            "
            @click="setFillMode('cep')"
          >
            Preencher com CEP
          </button>
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors"
            :class="
              fillMode === 'address'
                ? 'bg-white text-lais-primary shadow-sm'
                : 'text-lais-text-muted hover:text-lais-primary'
            "
            @click="setFillMode('address')"
          >
            Preencher com endereço
          </button>
        </div>
        <div class="flex flex-col gap-3 md:flex-row md:items-end">
          <LaisField
            class="flex-1"
            :label="fillMode === 'cep' ? 'CEP' : 'Endereço e número'"
            helper="Verifique se o imóvel está na área da Imudei ou se já está cadastrado."
          >
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                autocomplete="off"
                :class="inputClass"
                :placeholder="
                  fillMode === 'cep'
                    ? 'Digite o CEP'
                    : 'Digite e selecione um endereço com número'
                "
                @input="onSearchInput"
                @focus="onSearchFocus"
                @blur="onSearchBlur"
                @keydown.enter.prevent="normalize()"
              />
              <div
                v-if="
                  fillMode === 'address' &&
                  (isPredictionListOpen || isAutocompleteLoading)
                "
                class="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-lais-sm border border-lais-border bg-white py-1 shadow-lais-card"
              >
                <div
                  v-if="isAutocompleteLoading"
                  class="px-3 py-2 text-[12px] font-medium text-lais-text-muted"
                >
                  Buscando endereços...
                </div>
                <button
                  v-for="prediction in predictions"
                  :key="predictionPlaceId(prediction)"
                  type="button"
                  class="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-lais-bg-soft focus:bg-lais-bg-soft focus:outline-none"
                  @mousedown.prevent="selectPrediction(prediction)"
                >
                  <span class="text-[13px] font-semibold text-lais-text">
                    {{ predictionPrimaryText(prediction) }}
                  </span>
                  <span
                    v-if="predictionSecondaryText(prediction)"
                    class="text-[12px] text-lais-text-muted"
                  >
                    {{ predictionSecondaryText(prediction) }}
                  </span>
                </button>
              </div>
            </div>
          </LaisField>
          <div class="flex items-center gap-2">
            <LaisPrimaryButton
              icon="i-lucide-map-pinned"
              :is-loading="isNormalizing"
              @click="normalize"
            >
              Verificar endereço
            </LaisPrimaryButton>
            <LaisPrimaryButton
              v-if="fillMode === 'address'"
              variant="ghost"
              @click="emitValue({ sem_numero: true, numero: '' })"
            >
              Continuar sem número
            </LaisPrimaryButton>
            <span
              v-if="sourceLabel"
              class="inline-flex items-center rounded-full bg-lais-primary-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-lais-primary"
            >
              {{ sourceLabel }}
            </span>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <LaisField class="md:col-span-2" label="Endereço">
          <input
            :value="modelValue.endereco || ''"
            type="text"
            :class="inputClass"
            @input="emitValue({ endereco: $event.target.value })"
          />
        </LaisField>

        <LaisField :label="modelValue.sem_numero ? 'Número' : 'Número/Bloco'">
          <input
            :value="modelValue.numero || ''"
            type="text"
            :disabled="modelValue.sem_numero"
            :class="[
              inputClass,
              modelValue.sem_numero
                ? 'bg-lais-bg-soft text-lais-text-muted'
                : '',
            ]"
            @input="emitValue({ numero: $event.target.value })"
          />
          <label
            class="mt-1 flex w-fit items-center gap-2 text-[12px] font-semibold text-lais-text-body"
          >
            <input
              type="checkbox"
              :checked="modelValue.sem_numero || false"
              class="size-4 rounded accent-lais-primary"
              @change="toggleWithoutNumber"
            />
            Sem número
          </label>
        </LaisField>

        <LaisField label="Bairro">
          <input
            :value="modelValue.bairro || ''"
            type="text"
            :class="inputClass"
            @input="emitValue({ bairro: $event.target.value })"
          />
        </LaisField>

        <LaisField label="Cidade">
          <input
            :value="modelValue.cidade || ''"
            type="text"
            :class="inputClass"
            @input="emitValue({ cidade: $event.target.value })"
          />
        </LaisField>

        <LaisField label="Estado">
          <input
            :value="modelValue.uf || ''"
            type="text"
            maxlength="2"
            class="uppercase"
            :class="[inputClass]"
            @input="emitValue({ uf: $event.target.value })"
          />
        </LaisField>

        <LaisField label="CEP">
          <input
            :value="modelValue.cep || ''"
            type="text"
            :class="inputClass"
            @input="emitValue({ cep: $event.target.value })"
          />
        </LaisField>

        <LaisField label="Latitude">
          <input
            :value="modelValue.latitude ?? ''"
            type="number"
            step="0.000001"
            :class="inputClass"
            @input="emitValue({ latitude: $event.target.value })"
          />
        </LaisField>

        <LaisField label="Longitude">
          <input
            :value="modelValue.longitude ?? ''"
            type="number"
            step="0.000001"
            :class="inputClass"
            @input="emitValue({ longitude: $event.target.value })"
          />
        </LaisField>
      </div>
    </div>
  </div>
</template>
